import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  approveEvaluation,
  getEvaluationReview,
  regenerateEvaluation,
  saveReviewAnnotation,
} from '../../services/ExpertReviewService';
import { UserContext } from '../../utils/contexts/UserContext';

export default function ExpertReviewWorkspace() {
  const { uuid } = useParams();
  const { isExpert } = useContext(UserContext);
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [notes, setNotes] = useState({});
  const [instruction, setInstruction] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [selectedRevisionUuid, setSelectedRevisionUuid] = useState('');

  const load = useCallback(() => {
    return getEvaluationReview(uuid)
      .then((data) => {
        setReview(data);
        setNotes(
          Object.fromEntries(
            (data.annotations || []).map((item) => [item.response_uuid, item.note]),
          ),
        );
        setSelectedRevisionUuid((selected) =>
          data.revisions?.some((revision) => revision.uuid === selected)
            ? selected
            : data.current_revision || data.revisions?.at(-1)?.uuid || '',
        );
        return data;
      })
      .catch(() => setError('Unable to load this review.'));
  }, [uuid]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (review?.status !== 'generating') return undefined;
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
  }, [review?.status, load]);

  const currentRevision = useMemo(
    () =>
      review?.revisions?.find(
        (revision) => revision.uuid === review.current_revision,
      ) || review?.revisions?.at(-1),
    [review],
  );
  const displayedRevision = useMemo(
    () => review?.revisions?.find((revision) => revision.uuid === selectedRevisionUuid) || currentRevision,
    [currentRevision, review, selectedRevisionUuid],
  );
  const evaluations = useMemo(
    () =>
      Object.fromEntries(
        (displayedRevision?.output?.answers || []).map((item) => [
          item.response_uuid,
          item,
        ]),
      ),
    [displayedRevision],
  );

  const saveNote = async (response) => {
    setError('');
    setBusyAction(`note-${response.response_uuid}`);
    try {
      const result = await saveReviewAnnotation(review.uuid, {
        response_type: response.response_type,
        response_uuid: response.response_uuid,
        note: notes[response.response_uuid] || '',
        expected_version: review.version,
      });
      setReview((value) => ({ ...value, version: result.review_version }));
      setMessage('Expert note saved.');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save the note.');
      if (requestError.response?.status === 409) load();
    } finally {
      setBusyAction('');
    }
  };

  const regenerate = async () => {
    setError('');
    setBusyAction('regenerate');
    try {
      await regenerateEvaluation(review.uuid, {
        instruction,
        expected_version: review.version,
      });
      setMessage('Re-evaluation queued.');
      setInstruction('');
      await load();
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail || 'Unable to queue re-evaluation.',
      );
      if (requestError.response?.status === 409) load();
    } finally {
      setBusyAction('');
    }
  };

  const approve = async () => {
    if (!currentRevision || !window.confirm('Publish this evaluation to the student profile?')) return;
    setError('');
    setBusyAction('approve');
    try {
      const data = await approveEvaluation(review.uuid, {
        revision_uuid: currentRevision.uuid,
        expected_version: review.version,
      });
      setReview(data);
      setMessage('Evaluation approved and student characteristics updated.');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to approve.');
      if (requestError.response?.status === 409) load();
    } finally {
      setBusyAction('');
    }
  };

  if (!review && error) return <p role="alert" className="p-8 text-red-600">{error}</p>;
  if (!review) return <p role="status" className="p-8">Loading review…</p>;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold">
          {review.student.first_name} {review.student.last_name}
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          {review.interview.name} · status: {review.status.replaceAll('_', ' ')} · revision{' '}
          {displayedRevision?.sequence || '—'}
        </p>
        <button
          onClick={() => navigate(`/portfolio/${review.student.uuid}`)}
          className="mt-3 rounded border border-blue-600 px-4 py-2 font-medium text-blue-700 dark:text-blue-200"
        >
          View {isExpert ? 'scoped ' : ''}portfolio
        </button>
      </div>
      {!isExpert && (
        <p className="rounded bg-blue-50 p-3 text-blue-900">
          Organization administrators can inspect this draft and portfolio, but only an assigned expert can edit or approve the evaluation.
        </p>
      )}
      {message && <p className="rounded bg-green-100 p-3 text-green-800">{message}</p>}
      {error && <p role="alert" className="rounded bg-red-100 p-3 text-red-800">{error}</p>}
      {review.status === 'generating' && (
        <div role="status" className="rounded bg-blue-50 p-3 text-blue-900">AI re-evaluation is running. This workspace refreshes automatically.</div>
      )}
      {currentRevision?.status === 'failed' && (
        <div role="alert" className="rounded bg-red-100 p-3 text-red-800">
          <strong>Revision generation failed.</strong>{' '}{currentRevision.error_message || 'Review the instruction and try again.'}
        </div>
      )}

      <section className="rounded-xl border bg-white p-5 dark:bg-gray-800 dark:border-gray-700">
        <h2 className="text-xl font-semibold">Overall AI assessment</h2>
        {displayedRevision?.is_legacy && (
          <p className="my-2 text-amber-700">Legacy result: per-answer rationale was not stored.</p>
        )}
        <p className="mt-3 whitespace-pre-wrap">
          {displayedRevision?.output?.overall_summary || 'No successful AI draft yet.'}
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {(displayedRevision?.output?.skills || []).map((skill) => (
            <div key={skill.skill_uuid} className="rounded border p-3 dark:border-gray-600">
              <strong>{skill.name}: {skill.score}/100</strong>
              <p>{skill.summary}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Answers and expert guidance</h2>
        {review.responses.map((response) => {
          const evaluation = evaluations[response.response_uuid];
          return (
            <article key={response.response_uuid} className="rounded-xl border bg-white p-5 dark:bg-gray-800 dark:border-gray-700">
              <p className="font-semibold">
                {response.question?.body || response.question_text || 'Follow-up answer'}
              </p>
              {response.media_url && <audio className="my-3 w-full" controls src={response.media_url} />}
              <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-200">{response.transcript || 'No transcript.'}</p>
              <div className="mt-4 rounded bg-blue-50 p-4 text-gray-900">
                <strong>AI evaluation</strong>
                <p>{evaluation?.rationale || 'Not available in this revision.'}</p>
                {(evaluation?.evidence_excerpts || []).map((excerpt) => (
                  <blockquote key={excerpt} className="mt-2 border-l-4 border-blue-300 pl-3 italic">“{excerpt}”</blockquote>
                ))}
                {(evaluation?.skills || []).map((skill) => (
                  <p key={skill.skill_uuid} className="mt-2"><strong>{skill.name}: {skill.score}/100.</strong> {skill.rationale}</p>
                ))}
              </div>
              {isExpert && (
                <>
                  <label htmlFor={`note-${response.response_uuid}`} className="mt-4 block font-medium">Expert note for the next AI revision</label>
                  <textarea
                    id={`note-${response.response_uuid}`}
                    className="mt-2 min-h-24 w-full rounded border p-3 text-gray-900"
                    value={notes[response.response_uuid] || ''}
                    onChange={(event) => setNotes((value) => ({ ...value, [response.response_uuid]: event.target.value }))}
                  />
                  <button disabled={Boolean(busyAction) || review.status === 'generating'} onClick={() => saveNote(response)} className="mt-2 rounded bg-gray-700 px-4 py-2 text-white disabled:opacity-50">{busyAction === `note-${response.response_uuid}` ? 'Saving…' : 'Save note'}</button>
                </>
              )}
            </article>
          );
        })}
      </section>

      {isExpert && <section className="rounded-xl border bg-white p-5 dark:bg-gray-800 dark:border-gray-700">
        <label htmlFor="reevaluation-command" className="block text-lg font-semibold">Command for re-evaluation</label>
        <textarea
          id="reevaluation-command"
          className="mt-2 min-h-28 w-full rounded border p-3 text-gray-900"
          value={instruction}
          onChange={(event) => setInstruction(event.target.value)}
          placeholder="Explain what the AI should reconsider while keeping the supplied evidence and scoring rules."
        />
        <div className="mt-4 flex flex-wrap gap-3">
          <button disabled={!instruction || review.status === 'generating' || Boolean(busyAction)} onClick={regenerate} className="rounded bg-blue-600 px-5 py-2 text-white disabled:opacity-50">{busyAction === 'regenerate' ? 'Queuing…' : 'Generate new revision'}</button>
          <button disabled={currentRevision?.status !== 'succeeded' || review.status === 'generating' || Boolean(busyAction)} onClick={approve} className="rounded bg-green-600 px-5 py-2 text-white disabled:opacity-50">{busyAction === 'approve' ? 'Publishing…' : 'Approve and publish'}</button>
        </div>
      </section>}

      <section>
        <h2 className="text-xl font-semibold">Revision history</h2>
        <div className="mt-2 flex flex-wrap gap-2">
          {review.revisions.map((revision) => (
            <button
              type="button"
              aria-pressed={displayedRevision?.uuid === revision.uuid}
              onClick={() => setSelectedRevisionUuid(revision.uuid)}
              key={revision.uuid}
              className={`rounded border px-3 py-2 ${displayedRevision?.uuid === revision.uuid ? 'border-blue-600 bg-blue-50 text-blue-800' : ''}`}
            >
              #{revision.sequence} · {revision.status}{revision.is_legacy ? ' · legacy' : ''}
            </button>
          ))}
        </div>
        {displayedRevision?.uuid !== currentRevision?.uuid && (
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="rounded border p-3"><h3 className="font-semibold">Viewed revision #{displayedRevision.sequence}</h3><p className="mt-2 whitespace-pre-wrap text-sm">{displayedRevision.output?.overall_summary || 'No summary.'}</p></div>
            <div className="rounded border border-blue-300 bg-blue-50 p-3 text-gray-900"><h3 className="font-semibold">Current revision #{currentRevision?.sequence}</h3><p className="mt-2 whitespace-pre-wrap text-sm">{currentRevision?.output?.overall_summary || 'No summary.'}</p></div>
          </div>
        )}
      </section>
    </div>
  );
}
